import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { Link } from 'react-router-dom';
import { Send, MessageCircle, ChevronUp, ChevronDown, HelpCircle, X, XCircle, Mic, Loader2, Eye, RotateCcw, Settings, MapPin, Film, Lightbulb, ArrowRight } from 'lucide-react';
import { useContent } from './services/contentService';
import { ESCENARIOS as ESCENARIOS_CONFIG, AVATAR_ENGINE } from './config';
import apiService from './services/api';
import { useLocalSTT } from './hooks/useLocalSTT';
import { useLocalStorage } from './hooks/useLocalStorage';
import ChatMessage from './components/ChatMessage';
import ScenarioSelector from './components/ScenarioSelector';
import Sidebar from './components/Sidebar';
import AnimatedAvatar from './components/AnimatedAvatar';
import RiveAvatar from './components/RiveAvatar';
import LanguageSelector from './components/LanguageSelector';
import SkillSelector from './components/SkillSelector';
import FeedbackView from './components/FeedbackView';
import IntroductionScreen from './components/IntroductionScreen';
import ChatTutorial from './components/ChatTutorial';
import ScenarioDescription from './components/ScenarioDescription';
import { EVENTS as JOYRIDE_EVENTS, STATUS as JOYRIDE_STATUS, ACTIONS as JOYRIDE_ACTIONS } from 'react-joyride';
import BackgroundStack from './components/background/BackgroundStack';
import { I18nProvider, useI18n } from './i18n';
import { reportCompletion } from './services/completionReporter';
import OpenInNewTabBanner from './components/OpenInNewTabBanner';

/**
 * Detecta si un texto luce como el payload JSON de feedback ({sections:[...]}).
 * Sirve de fallback cuando el backend no manda `phase: feedback` en message_done.
 */
function looksLikeFeedbackJson(text) {
    if (!text) return false;
    let s = String(text).trim();
    const fenceMatch = s.match(/^```(?:json)?\s*([\s\S]*?)\s*```$/);
    if (fenceMatch) s = fenceMatch[1].trim();
    if (!s.startsWith('{')) return false;
    try {
        const parsed = JSON.parse(s);
        return !!(parsed && Array.isArray(parsed.sections) && parsed.sections.length > 0);
    } catch (_) {
        return false;
    }
}

/** Personaje del escenario: contrato backend `nombre_personaje` / `cargo_personaje`; se aceptan claves legadas por compatibilidad. */
function pickPersonajeEscenario(scenario) {
    if (!scenario) return { nombre_personaje: null, cargo_personaje: null };
    return {
        nombre_personaje: scenario.nombre_personaje ?? scenario.interlocutor_nombre ?? null,
        cargo_personaje: scenario.cargo_personaje ?? scenario.interlocutor_rol ?? null,
    };
}

function AppInner({ onLangChange }) {
    const { t } = useI18n();
    // Contenido dinámico desde ContentContext (con fallback automático)
    const { content, isLoadingContent } = useContent();

    // Estados principales
    const [messages, setMessages] = useState([]);
    const [selectedScenario, setSelectedScenario] = useState(null);
    const [scenarioInfo, setScenarioInfo] = useState(null);
    const [isFirstMessage, setIsFirstMessage] = useState(true);
    const [currentPhase, setCurrentPhase] = useLocalStorage('current_phase', 'recepcion');
    const [currentSkill, setCurrentSkill] = useState('');
    const [evaluatedSkills, setEvaluatedSkills] = useState([]);
    const [pendingSkills, setPendingSkills] = useState([]);
    const [turnosDesafio, setTurnosDesafio] = useState(1);
    const [feedbackMessage, setFeedbackMessage] = useLocalStorage('feedback_message', '');
    const [inputMessage, setInputMessage] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [isInitialized, setIsInitialized] = useState(false);
    const [selectedLanguage, setSelectedLanguageLocal] = useState('es');
    const setSelectedLanguage = useCallback((lang) => {
        setSelectedLanguageLocal(lang);
        onLangChange?.(lang);
    }, [onLangChange]);

    const contentSlice = useMemo(() => {
        if (!content?.byLang) return content;
        const L = selectedLanguage === 'en' ? 'en' : 'es';
        return content.byLang[L] || content.byLang.es;
    }, [content, selectedLanguage]);
    const ESCENARIOS = contentSlice?.escenarios || {};

    const [selectedSkills, setSelectedSkills] = useState([]);
    const [selectedLevel, setSelectedLevel] = useState(null);
    const [showSkillSelector, setShowSkillSelector] = useState(false);
    const [showIntroduction, setShowIntroduction] = useState(false);
    const [isSelectingScenario, setIsSelectingScenario] = useState(false);
    // Hint overlay: aparece brevemente cerca del avatar; el bombillo lo recuerda
    const [hintOverlayVisible, setHintOverlayVisible] = useState(false);
    const [lastSeenHint, setLastSeenHint] = useState('');
    const [hintHistory, setHintHistory] = useState([]);
    const hintTimerRef = useRef(null);
    const sessionStartRef = useRef(Date.now());
    const [isConfigOpen, setIsConfigOpen] = useState(false);
    const [newThreadId, setNewThreadId] = useState('');
    const [showHelpModal, setShowHelpModal] = useState(false);
    const [isChatOpen, setIsChatOpen] = useState(false);
    const [sidebarExpanded, setSidebarExpanded] = useState(false);
    const [lastMessageWasVoice, setLastMessageWasVoice] = useState(false);
    const [isVoiceTranscribing, setIsVoiceTranscribing] = useState(false);
    const [hud, setHud] = useState(null);
    const [challengeLabel, setChallengeLabel] = useState('');
    const [challengeIndex, setChallengeIndex] = useState(null);
    const [challengePoolDetail, setChallengePoolDetail] = useState(null);
    const [masterySignals, setMasterySignals] = useState(null);
    const [criteriaDetail, setCriteriaDetail] = useState(null);
    const [progress, setProgress] = useState(0);
    const [stance, setStance] = useState('');
    const [bodyLanguage, setBodyLanguage] = useState('');
    const [isFeedbackTransitioning, setIsFeedbackTransitioning] = useState(false);
    const [hint, setHint] = useState('');
    const [turnosMinimos, setTurnosMinimos] = useState(5);
    const [turnosMaximos, setTurnosMaximos] = useState(7);

    // Contexto escénico (scene_moment, visit_stage, scene_moments_shown)
    const [sceneMoment, setSceneMoment] = useState('');
    const [sceneMomentId, setSceneMomentId] = useState('');
    const [visitStage, setVisitStage] = useState('');
    const [sceneMomentsShown, setSceneMomentsShown] = useState([]);
    const [activeSceneMoment, setActiveSceneMoment] = useState('');
    const sceneMomentTimerRef = useRef(null);
    // Set síncrono con los scene_moment_id ya volcados al chat (evita duplicados
    // cuando el mismo scene llega primero por SSE y luego en el payload final de onDone).
    const renderedSceneIdsRef = useRef(new Set());
    // Flag: si onScene ya actualizó visit_stage durante este stream, onDone no lo sobreescribe.
    const visitStageFromSceneRef = useRef(false);

    // Tutorial states
    const [tutorialCompleted, setTutorialCompleted] = useLocalStorage('chat_tutorial_completed_v1', false);
    const [tutorialRun, setTutorialRun] = useState(false);
    const [tutorialStepIndex, setTutorialStepIndex] = useState(0);
    const [tutorialForceStopAudio, setTutorialForceStopAudio] = useState(false);
    const [tutorialForceHint, setTutorialForceHint] = useState(false);
    const [tutorialForceTimeline, setTutorialForceTimeline] = useState(false);
    const [tutorialForceAvatarLabels, setTutorialForceAvatarLabels] = useState(false);

    // Briefing card (mostrado antes del primer mensaje)
    const [briefingDismissed, setBriefingDismissed] = useState(false);

    // localStorage hooks
    const [threadId, setThreadId] = useLocalStorage('thread_id', null);
    const [storedScenario, setStoredScenario] = useLocalStorage('scenario', null);
    const [storedSkills, setStoredSkills] = useLocalStorage('selected_skills', []);
    const [storedLevel, setStoredLevel] = useLocalStorage('selected_level', null);

    // Referencias
    const messagesEndRef = useRef(null);
    const chatContainerRef = useRef(null);
    const inputRef = useRef(null);
    const ttsAudioRef = useRef(null);
    // Ref sincronizado con currentPhase para uso dentro de closures de streaming
    const phaseRef = useRef(currentPhase);

    const stopTTS = React.useCallback(() => {
        try {
            if (ttsAudioRef.current) {
                ttsAudioRef.current.pause();
                ttsAudioRef.current.currentTime = 0;
                ttsAudioRef.current = null;
            }
        } catch (_) {}
        setIsTtsPlaying(false);
    }, []);

    // Reproducir TTS usando API REST
    const playTTS = React.useCallback(async (text) => {
        try {
            if (!text || !text.trim()) return;
            if (ttsAudioRef.current) {
                try {
                    ttsAudioRef.current.pause();
                    ttsAudioRef.current.currentTime = 0;
                } catch (_) {}
                ttsAudioRef.current = null;
            }
            setIsTtsLoading(true);
            const audioBlob = await apiService.textToSpeech(text, selectedLanguage);
            const audioUrl = URL.createObjectURL(audioBlob);
            const audio = new Audio(audioUrl);
            ttsAudioRef.current = audio;
            setIsTtsLoading(false);
            setIsTtsPlaying(true);
            audio.onended = () => {
                setIsTtsPlaying(false);
                ttsAudioRef.current = null;
            };
            await audio.play();
            addLog('🔊 Reproducido', 'success');
        } catch (error) {
            console.error('Error al reproducir audio:', error);
            setIsTtsLoading(false);
            setIsTtsPlaying(false);
        }
    }, []);

    // Estados de TTS local (via REST)
    const [isTtsPlaying, setIsTtsPlaying] = useState(false);
    const [isTtsLoading, setIsTtsLoading] = useState(false);
    const [micPermissionDenied, setMicPermissionDenied] = useState(false);
    const [logs, setLogs] = useState([]);
    const addLog = (message, type = 'info') => setLogs(prev => [...prev, { message, type, timestamp: new Date().toISOString() }]);
    const clearLogs = () => setLogs([]);

    // Transcripción local (vista previa) para feedback inmediato
    const { isSupported: isLocalSTTSupported, isActive: isLocalSTTActive, previewTranscript, start: startLocalSTT, stop: stopLocalSTT, reset: resetLocalSTT } = useLocalSTT(selectedLanguage);

    // No auto-iniciar STT: solo detener si hay TTS en reproducción
    useEffect(() => {
        if (isTtsPlaying && isLocalSTTActive) {
            stopLocalSTT();
        }
    }, [isTtsPlaying, isLocalSTTActive, stopLocalSTT]);

    // Reflejar la vista previa en el input mientras se está transcribiendo
    useEffect(() => {
        if (!isLocalSTTActive) return; // si no está activo, no tocar el input
        // Si acabamos de enviar (input vacío), no reinyectar texto hasta que haya nuevo audio
        if (inputMessage === '' && !previewTranscript) return;
        if (previewTranscript && !isVoiceTranscribing) {
            setInputMessage(previewTranscript);
        }
    }, [isLocalSTTActive, previewTranscript, isVoiceTranscribing, inputMessage]);

    // Auto-resize del textarea:
    // - Crece libremente hasta MAX_INPUT_HEIGHT.
    // - Al superar ese límite activa scroll interno y auto-scrollea al final
    //   para que siempre se vea lo más reciente (especialmente en voz).
    // Nota: el límite se eligió para no tapar el botón de micrófono cuando
    // la transcripción de voz va escribiendo texto largo en el campo.
    const MAX_INPUT_HEIGHT = 110; // px ≈ 4 líneas
    useEffect(() => {
        const el = inputRef.current;
        if (!el) return;
        el.style.height = 'auto';
        const natural = el.scrollHeight;
        if (natural <= MAX_INPUT_HEIGHT) {
            el.style.height = `${natural}px`;
            el.style.overflowY = 'hidden';
        } else {
            el.style.height = `${MAX_INPUT_HEIGHT}px`;
            el.style.overflowY = 'auto';
            // Scroll al final para ver el texto más reciente
            el.scrollTop = el.scrollHeight;
        }
    }, [inputMessage]);

    // Limpiar vista previa cuando llega transcripción del servidor
    useEffect(() => {
        if (isVoiceTranscribing && isLocalSTTActive) {
            stopLocalSTT();
        }
    }, [isVoiceTranscribing, isLocalSTTActive, stopLocalSTT]);

    // Atajo de teclado: espacio activa/desactiva el micrófono
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.code !== 'Space') return;
            // No interceptar si el foco está en un campo de texto o botón
            const tag = document.activeElement?.tagName;
            if (tag === 'TEXTAREA' || tag === 'INPUT' || tag === 'BUTTON') return;
            if (currentPhase === 'feedback') return;
            if (!isInitialized) return;
            e.preventDefault();
            if (isLocalSTTActive) {
                stopLocalSTT();
            } else {
                startLocalSTT();
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isLocalSTTActive, startLocalSTT, stopLocalSTT, isInitialized, currentPhase]);

    // Función para manejar mensajes de voz
    function handleVoiceMessage(voiceData) {
        if (voiceData) {
            setBriefingDismissed(true);
            // Agregar mensaje del usuario
            setMessages(prev => [...prev, {
                role: 'user',
                content: voiceData.user
            }]);

            // Agregar respuesta del asistente
            setMessages(prev => [...prev, {
                role: 'assistant',
                content: voiceData.assistant
            }]);

            // Actualizar estado de la simulación
            if (voiceData.fullResponse) {
                updateSimulationState(voiceData.fullResponse);
            }
        }
    }

    // Función para actualizar el estado de la simulación
    const updateSimulationState = (response) => {
        if (response.status === 'success') {
            console.log('📊 [updateSimulationState] Payload completo:', JSON.stringify(response, null, 2));
            console.log('📊 [updateSimulationState] Campos de progresión:', {
                phase: response.phase,
                progress: response.progress,
                challenge_index: response.challenge_index,
                challenge_label: response.challenge_label,
                challenge_pool_detail: response.challenge_pool_detail,
                criteria_detail: response.criteria_detail,
                turnos_desafio: response.turnos_desafio,
                turnos_minimos: response.turnos_minimos,
                turnos_maximos: response.turnos_maximos,
                habilidad_actual: response.habilidad_actual,
                habilidades_evaluadas: response.habilidades_evaluadas,
                habilidades_pendientes: response.habilidades_pendientes,
                mastery_signals: response.mastery_signals,
            });

            if (response.phase)              setCurrentPhase(response.phase);
            if (response.habilidad_actual)   setCurrentSkill(response.habilidad_actual);

            if (Array.isArray(response.habilidades_evaluadas) && response.habilidades_evaluadas.length > 0)
                setEvaluatedSkills(response.habilidades_evaluadas);
            if (Array.isArray(response.habilidades_pendientes) && response.habilidades_pendientes.length > 0)
                setPendingSkills(response.habilidades_pendientes);

            if (response.turnos_desafio  != null) setTurnosDesafio(response.turnos_desafio);
            if (response.turnos_minimos  != null) setTurnosMinimos(response.turnos_minimos);
            if (response.turnos_maximos  != null) setTurnosMaximos(response.turnos_maximos);
            if (response.progress        != null) setProgress(response.progress);
            if (response.stance)                  setStance(response.stance);
            if (response.body_language)           setBodyLanguage(response.body_language);
            if (response.hint)                   setHint(response.hint);

            console.log('🎭 stance:', response.stance, '| body_language:', response.body_language, '| hint:', response.hint);

            if (response.challenge_label     != null) setChallengeLabel(response.challenge_label);
            if (response.challenge_index     != null) setChallengeIndex(response.challenge_index);
            if (response.challenge_pool_detail != null) setChallengePoolDetail(response.challenge_pool_detail);
            if (response.mastery_signals     != null) setMasterySignals(response.mastery_signals);
            if (response.criteria_detail     != null) setCriteriaDetail(response.criteria_detail);

            if (response.visit_stage) {
                if (visitStageFromSceneRef.current) {
                    console.log('🎬 [updateSimulationState] visit_stage ignorado (ya actualizado por onScene):', response.visit_stage);
                } else {
                    console.log('🎬 [updateSimulationState] Actualizando visit_stage:', response.visit_stage);
                    setVisitStage(response.visit_stage);
                }
            }
            if (Array.isArray(response.scene_moments_shown)) setSceneMomentsShown(response.scene_moments_shown);

            processSceneMoment({
                scene_moment: response.scene_moment,
                scene_moment_id: response.scene_moment_id,
            });

            if (response.feedbackMessage) setFeedbackMessage(response.feedbackMessage);
        }
    };

    // Helper centralizado: añade el divider de scene_moment al chat si el id aún
    // no fue pintado (dedupe vía ref síncrono). Usado desde onScene (SSE) y
    // desde updateSimulationState (payload final de onDone) sin duplicar.
    const processSceneMoment = useCallback((data) => {
        if (!data) return;
        const incomingSceneId = data.scene_moment_id || '';
        const incomingSceneText = (data.scene_moment || '').trim();
        if (!incomingSceneText || !incomingSceneId) return;
        if (renderedSceneIdsRef.current.has(incomingSceneId)) return;

        renderedSceneIdsRef.current.add(incomingSceneId);
        setSceneMoment(incomingSceneText);
        setSceneMomentId(incomingSceneId);
        setActiveSceneMoment(incomingSceneText);

        setMessages(prev => [...prev, {
            role: 'scene',
            content: incomingSceneText,
            sceneId: incomingSceneId,
        }]);

        if (sceneMomentTimerRef.current) clearTimeout(sceneMomentTimerRef.current);
        sceneMomentTimerRef.current = setTimeout(() => setActiveSceneMoment(''), 14000);
    }, []);

    // Limpiar timer del banner escénico al desmontar
    useEffect(() => {
        return () => {
            if (sceneMomentTimerRef.current) clearTimeout(sceneMomentTimerRef.current);
            if (hintTimerRef.current) clearTimeout(hintTimerRef.current);
        };
    }, []);

    // Humanizar etiquetas de etapas y momentos escénicos
    const getVisitStageLabel = (stage) => {
        const labels = selectedLanguage === 'en' ? {
            // Stages del Excel EN
            presentation: 'Presentation',
            initial_conversation: 'Initial Conversation',
            walkthrough: 'Site Walkthrough',
            closing: 'Closing',
            // Slugs genéricos por si el backend usa los mismos que ES
            entrada: 'Entrance',
            recepcion: 'Reception',
            saludo: 'Greeting',
            conversacion_inicial: 'Initial Conversation',
            recorrido: 'Site Walkthrough',
            instalaciones: 'Facilities',
            inspeccion: 'Inspection',
            oficina: 'Office',
            cierre: 'Closing',
            despedida: 'Farewell',
            seguimiento: 'Follow-up',
        } : {
            // Stages del Excel ES (incluye con mayúscula/acento tal como vienen)
            'Presentación': 'Presentación',
            presentation: 'Presentación',
            entrada: 'Entrada',
            recepcion: 'Recepción',
            saludo: 'Saludo',
            conversacion_inicial: 'Conversación inicial',
            initial_conversation: 'Conversación inicial',
            recorrido: 'Recorrido',
            walkthrough: 'Recorrido',
            instalaciones: 'Instalaciones',
            inspeccion: 'Inspección',
            oficina: 'Oficina',
            cierre: 'Cierre',
            closing: 'Cierre',
            despedida: 'Despedida',
            seguimiento: 'Seguimiento',
        };
        if (!stage) return '';
        return labels[stage] || stage.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
    };

    // Función para cargar mensajes previos
    const loadPreviousMessages = async (targetThreadId) => {
        try {
            const data = await apiService.loadPreviousMessages(targetThreadId);
            
            console.log('📡 Respuesta del backend:', data);
            
            // Verificar si hay error en la respuesta del backend
            if (data.checkpoints && data.checkpoints.status === 'error') {
                console.error('❌ Error del backend:', data.checkpoints.message);
                return false;
            }
            
            // Parser tolerante: el backend puede devolver el checkpoint en varias
            // formas. Probamos primero las rutas conocidas y, si ninguna coincide,
            // buscamos recursivamente el primer objeto que tenga "messages" (array)
            // y "phase" (string), que son los dos campos mínimos que usamos.
            const looksLikeCheckpoint = (obj) =>
                obj &&
                typeof obj === 'object' &&
                Array.isArray(obj.messages) &&
                (typeof obj.phase === 'string' || typeof obj.phase === 'undefined');

            const findCheckpointDeep = (node, depth = 0) => {
                if (!node || typeof node !== 'object' || depth > 4) return null;
                if (looksLikeCheckpoint(node)) return node;
                if (Array.isArray(node)) {
                    for (const item of node) {
                        const found = findCheckpointDeep(item, depth + 1);
                        if (found) return found;
                    }
                    return null;
                }
                for (const key of Object.keys(node)) {
                    const found = findCheckpointDeep(node[key], depth + 1);
                    if (found) return found;
                }
                return null;
            };

            let lastCheckpoint = null;
            const candidatePaths = [
                data?.last_checkpoint,
                data?.checkpoints?.last_checkpoint,
                data?.checkpoints?.checkpoint,
                data?.checkpoints?.state,
                data?.state,
                Array.isArray(data?.checkpoints)
                    ? data.checkpoints[data.checkpoints.length - 1]
                    : null,
                Array.isArray(data?.checkpoints?.checkpoints)
                    ? data.checkpoints.checkpoints[data.checkpoints.checkpoints.length - 1]
                    : null,
            ];
            for (const candidate of candidatePaths) {
                if (looksLikeCheckpoint(candidate)) {
                    lastCheckpoint = candidate;
                    break;
                }
            }
            if (!lastCheckpoint) {
                lastCheckpoint = findCheckpointDeep(data);
            }

            if (lastCheckpoint && Array.isArray(lastCheckpoint.messages)) {
                console.log('✅ Checkpoint encontrado, restaurando estado completo...');
                
                // Transformar mensajes
                const transformedMessages = lastCheckpoint.messages.map(m => ({
                    role: m.role || (m.type === 'human' ? 'user' : 'assistant'),
                    content: m.content || ''
                }));

                console.log(`📝 Restaurando ${transformedMessages.length} mensajes`);
                setMessages(transformedMessages);

                // Detectar feedback por contenido si el backend no marcó la fase.
                const lastAssistantMsg = [...transformedMessages].reverse().find(m => m.role === 'assistant');
                const lastLooksLikeFeedback = looksLikeFeedbackJson(lastAssistantMsg?.content);
                const inferredPhase = lastCheckpoint.phase
                    || (lastLooksLikeFeedback ? 'feedback' : 'recepcion');

                console.log(`🔄 Restaurando fase: ${inferredPhase}`);
                setCurrentPhase(inferredPhase);
                setCurrentSkill(lastCheckpoint.habilidad_actual || '');
                setEvaluatedSkills(Array.isArray(lastCheckpoint.habilidades_evaluadas) ? lastCheckpoint.habilidades_evaluadas : []);
                setPendingSkills(Array.isArray(lastCheckpoint.habilidades_pendientes) ? lastCheckpoint.habilidades_pendientes : []);
                setTurnosDesafio(lastCheckpoint.turnos_desafio || 1);
                if (lastCheckpoint.feedbackMessage) {
                    setFeedbackMessage(lastCheckpoint.feedbackMessage);
                } else if (inferredPhase === 'feedback' && lastAssistantMsg?.content) {
                    setFeedbackMessage(lastAssistantMsg.content);
                } else {
                    setFeedbackMessage('');
                }
                setChallengeLabel(lastCheckpoint.challenge_label || '');
                setChallengeIndex(lastCheckpoint.challenge_index ?? null);
                setChallengePoolDetail(lastCheckpoint.challenge_pool_detail ?? null);
                setMasterySignals(lastCheckpoint.mastery_signals ?? null);
                setCriteriaDetail(lastCheckpoint.criteria_detail ?? null);
                setProgress(lastCheckpoint.progress ?? 0);
                setStance(lastCheckpoint.stance || '');
                setBodyLanguage(lastCheckpoint.body_language || '');
                setHint(lastCheckpoint.hint || '');
                setTurnosMinimos(lastCheckpoint.turnos_minimos ?? 2);
                setTurnosMaximos(lastCheckpoint.turnos_maximos ?? 3);
                setVisitStage(lastCheckpoint.visit_stage || '');
                setSceneMomentsShown(Array.isArray(lastCheckpoint.scene_moments_shown) ? lastCheckpoint.scene_moments_shown : []);
                setSceneMoment(lastCheckpoint.scene_moment || '');
                setSceneMomentId(lastCheckpoint.scene_moment_id || '');
                setActiveSceneMoment('');

                // Inicializar el set de scene_ids ya renderizados con el último
                // visto para que un re-envío del mismo id no vuelva a pintarlo.
                renderedSceneIdsRef.current = new Set();
                if (lastCheckpoint.scene_moment_id) {
                    renderedSceneIdsRef.current.add(lastCheckpoint.scene_moment_id);
                }
                if (Array.isArray(lastCheckpoint.scene_moments_shown)) {
                    for (const id of lastCheckpoint.scene_moments_shown) {
                        if (id) renderedSceneIdsRef.current.add(id);
                    }
                }

                // Restaurar habilidades seleccionadas si están disponibles
                if (lastCheckpoint.habilidades_seleccionadas) {
                    setSelectedSkills(lastCheckpoint.habilidades_seleccionadas);
                }
                
                setIsFirstMessage(false);
                setIsInitialized(true);
                setBriefingDismissed(true);

                // Cargar información del escenario
                if (lastCheckpoint.escenario) {
                    console.log(`🎭 Restaurando escenario: ${lastCheckpoint.escenario}`);
                    const scenarioId = Object.keys(ESCENARIOS).find(id => 
                        ESCENARIOS[id].nombre === lastCheckpoint.escenario
                    );
                    if (scenarioId) {
                        setSelectedScenario(scenarioId);
                        const escRest = ESCENARIOS[scenarioId];
                        const pj = pickPersonajeEscenario(escRest);
                        setScenarioInfo({
                            nombre: lastCheckpoint.escenario,
                            tipo_proyecto: lastCheckpoint.tipo_proyecto || escRest.tipo_proyecto,
                            descripcion_breve: lastCheckpoint.descripcion_breve || escRest.descripcion_breve,
                            descripcion_detallada: lastCheckpoint.descripcion_detallada || escRest.descripcion_detallada,
                            instrucciones_actuacion: lastCheckpoint.instrucciones_actuacion || escRest.instrucciones_actuacion,
                            nombre_personaje:
                                lastCheckpoint.nombre_personaje
                                ?? lastCheckpoint.interlocutor_nombre
                                ?? pj.nombre_personaje,
                            cargo_personaje:
                                lastCheckpoint.cargo_personaje
                                ?? lastCheckpoint.interlocutor_rol
                                ?? pj.cargo_personaje,
                        });
                        console.log(`✅ Escenario restaurado: ${scenarioId}`);
                    }
                } else {
                    console.log('⚠️ No hay información de escenario en el checkpoint');
                }

                return true;
            } else {
                console.warn('⚠️ No se encontró checkpoint válido en la respuesta');
                console.warn('📊 Estructura recibida:', {
                    topLevelKeys: Object.keys(data || {}),
                    checkpointsType: Array.isArray(data?.checkpoints) ? 'array' : typeof data?.checkpoints,
                    checkpointsKeys:
                        data?.checkpoints && typeof data.checkpoints === 'object' && !Array.isArray(data.checkpoints)
                            ? Object.keys(data.checkpoints)
                            : 'N/A',
                    lastCheckpointShape: lastCheckpoint
                        ? {
                              keys: Object.keys(lastCheckpoint),
                              hasMessages: Array.isArray(lastCheckpoint.messages),
                              messagesLen: Array.isArray(lastCheckpoint.messages) ? lastCheckpoint.messages.length : null,
                          }
                        : null,
                });
                return false;
            }
        } catch (error) {
            console.error('Error al cargar mensajes:', error);
            return false;
        }
    };

    // Función para enviar mensaje de texto
    // Función para verificar si el feedback ya fue enviado
    const isFeedbackCompleted = () => {
        return currentPhase === 'feedback' && feedbackMessage && feedbackMessage.trim() !== '';
    };

    // Función para verificar si la simulación está en fase de feedback
    const isInFeedbackPhase = () => {
        return currentPhase === 'feedback';
    };

    // Función para verificar si se debe mostrar el input
    const shouldShowInput = () => {
        return isInitialized && !isInFeedbackPhase() && !isFeedbackCompleted();
    };

    // Wrapper del boton de mic: detecta si el navegador tiene el permiso
    // denegado y muestra un banner con los pasos para desbloquearlo. Si el
    // estado es 'prompt' o 'granted', delega en startLocalSTT (el motor STT
    // dispara el prompt nativo del navegador si hace falta).
    const handleMicClick = async () => {
        if (isLocalSTTActive) {
            stopLocalSTT();
            return;
        }
        try {
            if (navigator.permissions?.query) {
                const status = await navigator.permissions.query({ name: 'microphone' });
                if (status.state === 'denied') {
                    setMicPermissionDenied(true);
                    return;
                }
            }
        } catch (_) {
            // Permissions API no soportada (Safari viejo, etc.): seguimos al
            // flujo normal — si el navegador deniega, el motor STT fallara y
            // el usuario podra reintentar.
        }
        setMicPermissionDenied(false);
        startLocalSTT();
    };

    const sendMessage = async (messageText, { silent = false } = {}) => {
        if (!messageText.trim() || isLoading || !shouldShowInput()) return;

        setIsLoading(true);
        setBriefingDismissed(true);
        const isFirstMsg = messages.length === 0;
        const wasVoiceMessage = lastMessageWasVoice;

        console.log('📤 sendMessage - Estado de detección de voz:');
        console.log('  - lastMessageWasVoice:', lastMessageWasVoice);
        console.log('  - wasVoiceMessage:', wasVoiceMessage);
        console.log('  - messageText:', messageText);

        setLastMessageWasVoice(false);

        // Cortar audio previo si lo había (evita solapamientos con el turno anterior)
        if (ttsAudioRef.current) {
            try {
                ttsAudioRef.current.pause();
                ttsAudioRef.current.currentTime = 0;
            } catch (_) {}
            ttsAudioRef.current = null;
        }

        try {
            // Agregar mensaje del usuario inmediatamente (a menos que sea
            // silent: usado por el saludo inicial automatico tras "Entendido"
            // para que el personaje abra la conversacion sin mostrar el "hola"
            // en el chatbox).
            if (!silent) {
                const userMessage = {
                    role: 'user',
                    content: messageText,
                    _voiceMessage: wasVoiceMessage
                };
                setMessages(prev => [...prev, userMessage]);
            }
            setInputMessage('');
            stopLocalSTT();
            resetLocalSTT();
            setTimeout(() => setInputMessage(''), 0);
            setIsVoiceTranscribing(false);

            console.log('🔄 Enviando mensaje - /simulation/stream');
            visitStageFromSceneRef.current = false;
            const sessionDurationSeconds = parseFloat(((Date.now() - sessionStartRef.current) / 1000).toFixed(1));

            // ─── Pipeline "texto + audio por frases" ─────────────────────────
            // Mantenemos un buffer acumulativo con los tokens recibidos y, cada
            // vez que detectamos el final de una frase, encolamos un TTS para
            // esa frase. El reproductor consume la cola en orden.
            const sentenceEnd = /[.!?…](?:["'»)\]]*)(?:\s|$)/;
            const stState = { buffer: '', lastCut: 0 };
            const audioQueue = [];
            const playingState = { value: false };
            let assistantIdx = null;
            let firstTokenReceived = false;
            let accumulatedText = '';

            const extractCompleteSentences = (chunk) => {
                stState.buffer += chunk;
                const out = [];
                let match;
                while ((match = stState.buffer.slice(stState.lastCut).match(sentenceEnd))) {
                    const end = stState.lastCut + match.index + match[0].length;
                    const sentence = stState.buffer.slice(stState.lastCut, end).trim();
                    if (sentence) out.push(sentence);
                    stState.lastCut = end;
                }
                return out;
            };

            const fetchSentenceAudio = async (text) => {
                if (!text || !text.trim()) return null;
                try {
                    const blob = await apiService.textToSpeech(text, selectedLanguage);
                    return new Audio(URL.createObjectURL(blob));
                } catch (err) {
                    console.error('Error TTS frase:', err);
                    return null;
                }
            };

            const pumpQueue = async () => {
                if (playingState.value) return;
                playingState.value = true;
                setIsTtsLoading(false);
                setIsTtsPlaying(true);
                while (audioQueue.length > 0) {
                    // Limpiar cola y parar si se llega a la fase de feedback
                    if (phaseRef.current === 'feedback') {
                        audioQueue.length = 0;
                        break;
                    }
                    const audio = await audioQueue.shift();
                    if (!audio) continue;
                    if (phaseRef.current === 'feedback') continue; // audio ya cargado pero fase cambió
                    ttsAudioRef.current = audio;
                    await new Promise((resolve) => {
                        audio.onended = resolve;
                        audio.onerror = resolve;
                        audio.play().catch((e) => {
                            console.error('Error play:', e);
                            resolve();
                        });
                    });
                }
                playingState.value = false;
                setIsTtsPlaying(false);
                ttsAudioRef.current = null;
                addLog('🔊 Reproducido', 'success');
            };

            const enqueueSpeech = (text) => {
                if (!text || !text.trim()) return;
                setIsTtsLoading(true);
                audioQueue.push(fetchSentenceAudio(text));
                pumpQueue();
            };

            await apiService.sendMessageStream(
                threadId,
                messageText,
                isFirstMsg,
                scenarioInfo,
                selectedLanguage,
                selectedSkills,
                selectedLevel,
                {
                    onToken: ({ text }) => {
                        if (!text) return;
                        accumulatedText += text;

                        // No mostrar tokens ni reproducir audio en fase feedback
                        if (phaseRef.current === 'feedback') return;

                        // El payload de feedback llega como JSON o con code fence.
                        // El evento `phase: feedback` solo aparece en message_done,
                        // así que detectamos por la forma del texto para no
                        // pintar la burbuja ni arrancar TTS que después tendríamos
                        // que cortar abruptamente al cambiar de vista.
                        const head = accumulatedText.trimStart();
                        if (head.startsWith('{') || head.startsWith('```')) return;

                        if (!firstTokenReceived) {
                            firstTokenReceived = true;
                            setIsLoading(false);
                            setMessages(prev => {
                                assistantIdx = prev.length;
                                return [...prev, {
                                    role: 'assistant',
                                    content: text,
                                    _streaming: true
                                }];
                            });
                        } else {
                            setMessages(prev => {
                                if (assistantIdx == null || !prev[assistantIdx]) return prev;
                                const next = [...prev];
                                next[assistantIdx] = {
                                    ...next[assistantIdx],
                                    content: (next[assistantIdx].content || '') + text
                                };
                                return next;
                            });
                        }

                        for (const sentence of extractCompleteSentences(text)) {
                            enqueueSpeech(sentence);
                        }
                    },

                    onObserver: (data) => {
                        if (!data) return;
                        console.log('👁️ [onObserver] Data recibida:', JSON.stringify(data));
                        if (data.progress != null) setProgress(data.progress);
                        if (data.stance) setStance(data.stance);
                        if (data.body_language) setBodyLanguage(data.body_language);
                        if (data.hint != null) setHint(data.hint);
                        if (data.mastery_signals != null) setMasterySignals(data.mastery_signals);
                    },

                    onScene: (data) => {
                        if (!data) return;
                        console.log('🎬 [onScene] Data recibida:', JSON.stringify(data));
                        if (data.visit_stage) {
                            console.log('🎬 [onScene] Actualizando visit_stage:', data.visit_stage);
                            setVisitStage(data.visit_stage);
                            visitStageFromSceneRef.current = true;
                        }
                        if (Array.isArray(data.scene_moments_shown)) setSceneMomentsShown(data.scene_moments_shown);
                        processSceneMoment(data);
                    },

                    onMessageDone: ({ text, phase } = {}) => {
                        const fullText = (text && text.length >= accumulatedText.length) ? text : accumulatedText;

                        // Fallback: si el backend no manda phase='feedback' pero el contenido
                        // parsea como el JSON de feedback, forzamos la transición igual.
                        const isFeedback = phase === 'feedback' || looksLikeFeedbackJson(fullText);

                        if (isFeedback) {
                            // 1. Mostrar overlay de transición INMEDIATAMENTE
                            setIsFeedbackTransitioning(true);
                            // Mantener el overlay visible unos segundos para que el usuario
                            // pueda leer el mensaje de agradecimiento antes de pasar al feedback.
                            setTimeout(() => setIsFeedbackTransitioning(false), 3500);

                            // 2. Cortar TODO el audio: cola pendiente + audio en curso
                            phaseRef.current = 'feedback';
                            audioQueue.length = 0;
                            try {
                                if (ttsAudioRef.current) {
                                    ttsAudioRef.current.pause();
                                    ttsAudioRef.current.currentTime = 0;
                                    ttsAudioRef.current = null;
                                }
                            } catch (_) {}
                            playingState.value = false;
                            setIsTtsPlaying(false);
                            setIsTtsLoading(false);

                            // 3. Quitar la burbuja que muestra el texto crudo del feedback
                            if (assistantIdx != null) {
                                setMessages(prev => prev.filter((_, i) => i !== assistantIdx));
                            }

                            // 4. Setear fase y feedback message
                            setCurrentPhase('feedback');
                            if (fullText) setFeedbackMessage(fullText);
                            return;
                        }

                        // Flush: si quedó una cola sin terminar en punto, mándala a TTS igual
                        const leftover = fullText.slice(stState.lastCut).trim();
                        if (leftover) enqueueSpeech(leftover);
                        stState.buffer = '';
                        stState.lastCut = 0;

                        // Consolidar la burbuja con el texto final y quitar el flag de streaming
                        setMessages(prev => {
                            if (assistantIdx == null || !prev[assistantIdx]) return prev;
                            const next = [...prev];
                            next[assistantIdx] = {
                                ...next[assistantIdx],
                                content: fullText || next[assistantIdx].content,
                                _streaming: false,
                            };
                            return next;
                        });
                    },

                    onDone: (finalPayload) => {
                        if (finalPayload && finalPayload.status === 'success') {
                            updateSimulationState(finalPayload);
                        }
                        setIsFirstMessage(false);
                        setIsInitialized(true);
                    },

                    onError: ({ message, status } = {}) => {
                        console.error('Stream error:', status, message);
                        setMessages(prev => [...prev, {
                            role: 'assistant',
                            content: message || 'Lo siento, no pude procesar tu mensaje. Por favor, intenta de nuevo.'
                        }]);
                    },
                },
                undefined,
                sessionDurationSeconds,
            );
        } catch (error) {
            console.error('Error al enviar mensaje:', error);
            setMessages(prev => [...prev, {
                role: 'assistant',
                content: 'Error al comunicarse con el servidor. Por favor, verifica tu conexión.'
            }]);
        } finally {
            setIsLoading(false);
        }
    };

    // Función para manejar envío de mensaje
    const handleSubmit = (e) => {
        e.preventDefault();
        if (!isLoading && inputMessage.trim()) {
            sendMessage(inputMessage);
        }
    };

    // Función para seleccionar escenario
    const handleScenarioSelect = (scenarioId, scenario) => {
        setSelectedScenario(scenarioId);
        const pj = pickPersonajeEscenario(scenario);
        const pjCfg = pickPersonajeEscenario(ESCENARIOS_CONFIG[scenarioId]);
        setScenarioInfo({
            nombre: scenario.nombre,
            tipo_proyecto: scenario.tipo_proyecto,
            descripcion_breve: scenario.descripcion_breve,
            descripcion_detallada: scenario.descripcion_detallada,
            instrucciones_actuacion: scenario.instrucciones_actuacion,
            nombre_personaje: pj.nombre_personaje ?? pjCfg.nombre_personaje,
            cargo_personaje: pj.cargo_personaje ?? pjCfg.cargo_personaje,
        });
        setStoredScenario(scenarioId);
    };

    // Función para continuar desde el selector de escenario (con nivel incluido)
    const handleContinueFromScenario = () => {
        setIsSelectingScenario(false);
        // Crear thread si no existe o si el escenario cambió
        const sameScenarioAsBefore = storedScenario === selectedScenario && threadId;
        if (!sameScenarioAsBefore) {
            setThreadId(`thread-${uuidv4().slice(0, 8)}`);
        }
        setShowSkillSelector(true);
    };

    const handleContinueFromIntroduction = () => {
        setShowIntroduction(false);
    };

    // Función para manejar selección de habilidades
    const handleSkillsSelect = (skills) => {
        setSelectedSkills(skills);
        setStoredSkills(skills);
        setShowSkillSelector(false);
    };

    // Función para continuar después de seleccionar habilidades → ir directo a simulación
    const handleContinueFromSkills = () => {
        setShowSkillSelector(false);
        setIsInitialized(true);
    };

    // Función para manejar selección de nivel
    const handleLevelSelect = (level) => {
        setSelectedLevel(level);
        setStoredLevel(level);
    };

    // Función para volver de habilidades a escenario
    const handleBackToSkills = () => {
        setShowSkillSelector(true);
    };

    // Función para volver a selección de escenarios
    const handleBackToScenarios = () => {
        setShowSkillSelector(false);
        setShowIntroduction(false);
        setSelectedScenario(null);
        setScenarioInfo(null);
        setStoredScenario(null);
        setThreadId(null);
        setSelectedSkills([]);
        setStoredSkills([]);
        setSelectedLevel(null);
        setStoredLevel(null);
        setMessages([]); // Limpiar mensajes previos
        setIsFirstMessage(true); // Resetear flag de primer mensaje
        setCurrentPhase('recepcion'); // Resetear fase
        setCurrentSkill(''); // Limpiar habilidad actual
        setEvaluatedSkills([]); // Limpiar habilidades evaluadas
        setPendingSkills([]); // Limpiar habilidades pendientes
        setTurnosDesafio(1); // Resetear turnos
        setFeedbackMessage(''); // Limpiar mensaje de feedback
        setIsFeedbackTransitioning(false);
        setHud(null);
        setChallengeLabel('');
        setChallengeIndex(null);
        setChallengePoolDetail(null);
        setMasterySignals(null);
        setCriteriaDetail(null);
        setProgress(0);
        setStance('');
        setBodyLanguage('');
        setHint('');
        setHintHistory([]);
        setLastSeenHint('');
        setTurnosMinimos(5);
        setTurnosMaximos(7);
        setVisitStage('');
        setSceneMoment('');
        setSceneMomentId('');
        setSceneMomentsShown([]);
        setActiveSceneMoment('');
        renderedSceneIdsRef.current = new Set();
        setInputMessage('');
        setIsInitialized(false); // Resetear estado de inicialización
        setBriefingDismissed(false);
    };

    // Función para limpiar historial
    const handleClearHistory = () => {
        setMessages([]);
        setSelectedScenario(null);
        setScenarioInfo(null);
        setIsFirstMessage(true);
        setCurrentPhase('recepcion');
        setCurrentSkill('');
        setEvaluatedSkills([]);
        setPendingSkills([]);
        setTurnosDesafio(1);
        setFeedbackMessage('');
        setIsFeedbackTransitioning(false);
        setInputMessage('');
        setThreadId(null); // Limpiar threadId para volver al selector
        setStoredScenario(null);
        setSelectedSkills([]);
        setStoredSkills([]);
        setSelectedLevel(null);
        setStoredLevel(null);
        setShowSkillSelector(false);
        setShowIntroduction(true);
        setIsInitialized(false);
        setHud(null);
        setChallengeLabel('');
        setChallengeIndex(null);
        setChallengePoolDetail(null);
        setMasterySignals(null);
        setCriteriaDetail(null);
        setProgress(0);
        setStance('');
        setBodyLanguage('');
        setHint('');
        setHintHistory([]);
        setLastSeenHint('');
        setTurnosMinimos(5);
        setTurnosMaximos(7);
        setVisitStage('');
        setSceneMoment('');
        setSceneMomentId('');
        setSceneMomentsShown([]);
        setActiveSceneMoment('');
        renderedSceneIdsRef.current = new Set();
        setBriefingDismissed(false);
        clearLogs();
    };

    // Función para generar nuevo thread
    const handleNewThread = () => {
        const newThreadId = `thread-${uuidv4().slice(0, 8)}`;
        setThreadId(newThreadId);
        handleClearHistory();
    };

    // Función para cargar mensajes de un thread específico
    const handleLoadMessages = async (targetThreadId) => {
        if (targetThreadId && targetThreadId.trim() !== '') {
            console.log('🔄 handleLoadMessages iniciado para thread:', targetThreadId);
            
            // Limpiar solo el estado de la simulación, NO el escenario
            // El escenario se cargará desde el backend si existe
            setMessages([]);
            setCurrentPhase('recepcion');
            setCurrentSkill('');
            setEvaluatedSkills([]);
            setPendingSkills([]);
            setTurnosDesafio(1);
            setFeedbackMessage('');
            setHud(null);
            setChallengeLabel('');
            setChallengeIndex(null);
            setChallengePoolDetail(null);
            setMasterySignals(null);
            setCriteriaDetail(null);
            setProgress(0);
            setStance('');
            setBodyLanguage('');
            setHint('');
            setHintHistory([]);
            setLastSeenHint('');
            setTurnosMinimos(5);
            setTurnosMaximos(7);
            setVisitStage('');
            setSceneMoment('');
            setSceneMomentId('');
            setSceneMomentsShown([]);
            setActiveSceneMoment('');
            renderedSceneIdsRef.current = new Set();
            setInputMessage('');
            setIsFirstMessage(true);
            setIsInitialized(false);
            setBriefingDismissed(false);

            // Actualizar el thread ID (esto activará el useEffect de carga)
            console.log('🔄 Actualizando threadId a:', targetThreadId);
            setThreadId(targetThreadId);
            
            console.log('✅ Estado de simulación limpiado, el useEffect cargará TODO desde el backend');
        } else {
            console.log('⚠️ Thread ID inválido:', targetThreadId);
        }
    };

    // Scroll automático al final de los mensajes
    useEffect(() => {
        const container = chatContainerRef.current;
        if (container) {
            container.scrollTop = container.scrollHeight;
        }
    }, [messages, isLoading]);

    // Scroll al fondo cuando se abre el panel de chat
    useEffect(() => {
        if (isChatOpen) {
            // Espera un tick para que el DOM esté listo
            setTimeout(() => {
                const container = chatContainerRef.current;
                if (container) container.scrollTop = container.scrollHeight;
            }, 0);
        }
    }, [isChatOpen]);

    // Sincronizar phaseRef con currentPhase para closures de streaming
    useEffect(() => {
        phaseRef.current = currentPhase;
    }, [currentPhase]);

    // Detener sesión de voz automáticamente cuando se entra en fase de feedback
    useEffect(() => {
        if (isInFeedbackPhase() && isLocalSTTActive) {
            console.log('🛑 Entrando en fase de feedback - deteniendo captura local');
            stopLocalSTT();
            resetLocalSTT();
        }
    }, [currentPhase, isLocalSTTActive, stopLocalSTT, resetLocalSTT]);

    // Parar audio TTS al entrar en fase de feedback
    useEffect(() => {
        if (isInFeedbackPhase()) {
            try {
                if (ttsAudioRef.current) {
                    ttsAudioRef.current.pause();
                    ttsAudioRef.current.currentTime = 0;
                    ttsAudioRef.current = null;
                }
            } catch (_) {}
            setIsTtsPlaying(false);
            setIsTtsLoading(false);
        }
    }, [currentPhase]);

    // Reporte LTI: avisamos al servicio LTI (y este a Moodle via AGS) cuando
    // la simulacion termina. La funcion es idempotente por thread_id, asi
    // que recargas en fase feedback no disparan reportes duplicados.
    useEffect(() => {
        if (currentPhase === 'feedback') {
            reportCompletion();
        }
    }, [currentPhase]);

    // Mostrar hint overlay cuando llega una pista nueva y acumularla en historial
    useEffect(() => {
        if (hint && hint !== lastSeenHint) {
            setLastSeenHint(hint);
            setHintOverlayVisible(true);
            setHintHistory(prev => [
                ...prev.map(h => ({ ...h, active: false })),
                { text: hint, timestamp: Date.now(), active: true }
            ]);
            if (hintTimerRef.current) clearTimeout(hintTimerRef.current);
            hintTimerRef.current = setTimeout(() => setHintOverlayVisible(false), 20000);
        }
    }, [hint, lastSeenHint]);

    // Reproducir automáticamente el feedback cuando se recibe
    // (deshabilitado: el usuario elige cuándo escucharlo con el botón "Escuchar")


    // Cargar mensajes previos al iniciar o cuando cambie el thread ID
    useEffect(() => {
        if (threadId && threadId.trim() !== '') {
            console.log('🔄 useEffect detectó cambio en threadId:', threadId);
            setShowIntroduction(false); // No mostrar introducción si hay threadId

            // Si ya hay feedback completado persistido en localStorage, no recargar
            // desde backend porque el checkpoint sobreescribiría currentPhase y
            // feedbackMessage, sacándonos de la vista de feedback.
            if (currentPhase === 'feedback' && feedbackMessage && feedbackMessage.trim() !== '') {
                console.log('✅ Feedback persistido en localStorage; saltando carga de backend');
                setIsInitialized(true);
                return;
            }

            const loadMessages = async () => {
                // Usamos storedScenario (hidratado sincrónicamente desde localStorage)
                // en vez de selectedScenario, que aún puede ser null en el primer render.
                // Esto evita que al recargar con un thread vacío se muestre el selector
                // y el usuario acabe regenerando el thread_id por accidente.
                const hasScenario = !!(storedScenario && ESCENARIOS[storedScenario]);
                try {
                    const success = await loadPreviousMessages(threadId);
                    if (success) {
                        console.log('✅ Thread cargado exitosamente con todos sus datos');
                    } else {
                        console.log('⚠️ No se pudieron cargar mensajes del thread');
                        console.log('ℹ️ Esto puede ser porque:');
                        console.log('   - El thread no existe en el backend');
                        console.log('   - El thread no tiene mensajes previos');
                        console.log('   - Hubo un error en el backend');

                        if (hasScenario) {
                            console.log('✅ No hay mensajes previos pero hay escenario guardado. Habilitando simulación...');
                            setIsInitialized(true);
                        } else {
                            console.log('ℹ️ No hay escenario guardado, mostrando selector...');
                        }
                    }
                } catch (error) {
                    console.error('❌ Error en useEffect loadMessages:', error);
                    if (hasScenario) {
                        console.log('✅ Error en carga pero hay escenario guardado. Habilitando simulación...');
                        setIsInitialized(true);
                    }
                }
            };
            loadMessages();
        }
    }, [threadId]); // ✅ SOLO threadId como dependencia

    // Cargar escenario guardado.
    // IMPORTANTE: dependemos también de `content` porque ESCENARIOS sale de
    // useContent() y puede no estar listo en el primer render. Sin esta
    // dependencia, al recargar con un thread_id válido pero antes de que
    // termine de cargar el contenido, selectedScenario se quedaba en null
    // para siempre y el gatekeeper mostraba el selector de escenario.
    useEffect(() => {
        if (storedScenario && ESCENARIOS[storedScenario]) {
            const scenario = ESCENARIOS[storedScenario];
            setSelectedScenario(storedScenario);
            const pj = pickPersonajeEscenario(scenario);
            setScenarioInfo({
                nombre: scenario.nombre,
                tipo_proyecto: scenario.tipo_proyecto,
                descripcion_breve: scenario.descripcion_breve,
                descripcion_detallada: scenario.descripcion_detallada,
                instrucciones_actuacion: scenario.instrucciones_actuacion,
                nombre_personaje: pj.nombre_personaje,
                cargo_personaje: pj.cargo_personaje,
            });
            setIsInitialized(true);
        }
    }, [storedScenario, content, selectedLanguage]);

    // Cargar habilidades guardadas
    useEffect(() => {
        if (storedSkills && storedSkills.length > 0) {
            setSelectedSkills(storedSkills);
        }
    }, [storedSkills]);

    // Cargar nivel guardado
    useEffect(() => {
        if (storedLevel) {
            setSelectedLevel(storedLevel);
        }
    }, [storedLevel]);

    // Auto-disparar tutorial la primera vez que se entra al chat
    useEffect(() => {
        if (isInitialized && selectedScenario && threadId && !tutorialCompleted) {
            const timer = setTimeout(() => setTutorialRun(true), 500);
            return () => clearTimeout(timer);
        }
    }, [isInitialized, selectedScenario, threadId, tutorialCompleted]);

    // STEP_AVATAR_LABELS = 1, STEP_STOP_AUDIO = 3, STEP_CHAT_TOGGLE = 5, STEP_TIMELINE = 7, STEP_HINT = 10
    const handleTutorialStepChange = useCallback(({ stepIndex, action, type, status }) => {
        if (status === JOYRIDE_STATUS.FINISHED || status === JOYRIDE_STATUS.SKIPPED) {
            setTutorialRun(false);
            setTutorialStepIndex(0);
            setTutorialCompleted(true);
            setTutorialForceStopAudio(false);
            setTutorialForceHint(false);
            setTutorialForceTimeline(false);
            setTutorialForceAvatarLabels(false);
            setIsChatOpen(false);
            return;
        }

        if (type === JOYRIDE_EVENTS.STEP_AFTER || type === JOYRIDE_EVENTS.TARGET_NOT_FOUND) {
            const nextIndex = stepIndex + (action === JOYRIDE_ACTIONS.PREV ? -1 : 1);
            // Pre-configurar visibilidad forzada para el próximo paso
            setTutorialForceAvatarLabels(nextIndex === 1);
            setTutorialForceStopAudio(nextIndex === 3);
            setTutorialForceTimeline(nextIndex === 7);
            setTutorialForceHint(nextIndex === 10);
            setIsChatOpen(nextIndex === 5);
            setTutorialStepIndex(nextIndex);
        }
    }, [setTutorialCompleted]);

    const resetTutorial = useCallback(() => {
        setTutorialCompleted(false);
        setTutorialStepIndex(0);
        setShowHelpModal(false);
        setTimeout(() => setTutorialRun(true), 300);
    }, [setTutorialCompleted]);

    // Manejar la lógica de navegación inicial basada en threadId
    useEffect(() => {
        if (!threadId || threadId.trim() === '') {
            // No hay threadId en localStorage, mostrar introducción
            setShowIntroduction(true);
            setShowSkillSelector(false);
        } else {
            // Hay threadId en localStorage, ir directo al chat
            setShowIntroduction(false);
        }
    }, [threadId]);

    

    // Si se está mostrando la pantalla de introducción
    if (isLoadingContent) {
        return (
            <BackgroundStack className="flex min-h-screen w-full items-center justify-center" sidebarExpanded={false}>
                <div className="flex flex-col items-center gap-3 p-4">
                    <Loader2 className="w-8 h-8 animate-spin" style={{ color: '#1A4731' }} />
                    <p className="text-sm text-stone-500">{t('loadingConfig')}</p>
                </div>
            </BackgroundStack>
        );
    }

    // Overlay de transición a feedback: tiene prioridad incluso sobre el render
    // temprano de FeedbackView, para que el usuario vea el mensaje de
    // agradecimiento antes de aterrizar en el feedback.
    if (isFeedbackTransitioning) {
        return (
            <BackgroundStack className="flex min-h-screen w-full items-center justify-center" sidebarExpanded={false}>
                <div className="flex flex-col items-center gap-5 p-8 max-w-md text-center animate-fade-in">
                    <Loader2 className="w-12 h-12 animate-spin" style={{ color: '#1A4731' }} />
                    <h2 className="text-xl font-semibold text-stone-800">{t('thanksForParticipating')}</h2>
                    <p className="text-sm text-stone-600">{t('redirectingToFeedback')}</p>
                    <p className="text-xs text-stone-400">{t('pleaseWait')}</p>
                </div>
            </BackgroundStack>
        );
    }

    // Si el feedback ya estaba completado en una sesión anterior (persistido en localStorage),
    // mostrar FeedbackView inmediatamente sin pasar por intro/selector.
    if (currentPhase === 'feedback' && feedbackMessage && feedbackMessage.trim() !== '') {
        const HABILIDADES_DISPONIBLES_EARLY = contentSlice?.skills || {};
        const selectedSkillNamesEarly = selectedSkills.map(k =>
            HABILIDADES_DISPONIBLES_EARLY[k]?.nombre || k
        );
        return (
            <FeedbackView
                feedbackMessage={feedbackMessage}
                scenarioInfo={scenarioInfo}
                selectedSkills={selectedSkills}
                skillNames={selectedSkillNamesEarly}
                progress={progress}
                onRestart={handleBackToScenarios}
                isTtsPlaying={isTtsPlaying}
                isTtsLoading={isTtsLoading}
                onPlayTTS={playTTS}
                onStopTTS={stopTTS}
                selectedLanguage={selectedLanguage}
                onLanguageChange={setSelectedLanguage}
                threadId={threadId}
                selectedLevel={selectedLevel}
            />
        );
    }

    if (showIntroduction) {
        return (
            <IntroductionScreen
                onContinue={handleContinueFromIntroduction}
                selectedLanguage={selectedLanguage}
                onLanguageChange={setSelectedLanguage}
            />
        );
    }

    // Si se está mostrando el selector de habilidades
    if (showSkillSelector) {
        return (
            <SkillSelector
                selectedSkills={selectedSkills}
                onSkillsChange={setSelectedSkills}
                onContinue={handleContinueFromSkills}
                onBack={() => {
                    setShowSkillSelector(false);
                    setIsSelectingScenario(true);
                }}
                scenarioInfo={scenarioInfo}
                selectedLevel={selectedLevel}
                selectedLanguage={selectedLanguage}
                onLanguageChange={setSelectedLanguage}
            />
        );
    }

    // Guardia anti-flash: si hay thread + escenario guardados en localStorage
    // pero aún no se ha hidratado al estado (porque ESCENARIOS no cargó o el
    // useEffect no se disparó todavía), mostramos un loader en vez de llevar
    // al usuario al selector y provocar que pise el thread en curso.
    if (threadId && storedScenario && !selectedScenario) {
        return (
            <BackgroundStack className="flex min-h-screen w-full items-center justify-center" sidebarExpanded={false}>
                <div className="flex flex-col items-center gap-3 p-4">
                    <Loader2 className="w-8 h-8 animate-spin" style={{ color: '#1A4731' }} />
                    <p className="text-sm text-stone-500">{t('restoringConversation')}</p>
                </div>
            </BackgroundStack>
        );
    }

    // Si no hay threadId, no hay escenario, o se está navegando de vuelta desde SkillSelector
    if (!threadId || !selectedScenario || isSelectingScenario) {
        return (
            <ScenarioSelector
                selectedScenario={selectedScenario}
                onScenarioSelect={handleScenarioSelect}
                selectedLevel={selectedLevel}
                onLevelChange={(level) => { setSelectedLevel(level); setStoredLevel(level); }}
                onContinue={handleContinueFromScenario}
                onBack={() => setShowIntroduction(true)}
                selectedLanguage={selectedLanguage}
                onLanguageChange={setSelectedLanguage}
            />
        );
    }

    const getStanceLabel = (s) => {
        const labels = { supportive: t('supportive'), neutral: t('neutral'), resistant: t('resistant'), challenging: t('challenging'), open: t('open'), closed: t('closed'), skeptical: t('skeptical'), conciliatory: t('conciliatory'), firm: t('firm') };
        return labels[s] || s;
    };
    const getStanceColor = (s) => {
        if (s === 'supportive' || s === 'open' || s === 'conciliatory') return 'bg-green-50 text-green-700 border-green-200';
        if (s === 'neutral') return 'bg-amber-50 text-amber-700 border-amber-200';
        return 'bg-red-50 text-red-700 border-red-200';
    };


    // Nombres de habilidades seleccionadas (para FeedbackView)
    const HABILIDADES_DISPONIBLES_APP = contentSlice?.skills || {};
    const selectedSkillNames = selectedSkills.map(k =>
        HABILIDADES_DISPONIBLES_APP[k]?.nombre || k
    );

    // Mostrar FeedbackView cuando el feedback ya está listo
    if (isFeedbackCompleted() && feedbackMessage) {
        return (
            <FeedbackView
                feedbackMessage={feedbackMessage}
                scenarioInfo={scenarioInfo}
                selectedSkills={selectedSkills}
                skillNames={selectedSkillNames}
                progress={progress}
                onRestart={handleBackToScenarios}
                isTtsPlaying={isTtsPlaying}
                isTtsLoading={isTtsLoading}
                onPlayTTS={playTTS}
                onStopTTS={stopTTS}
                selectedLanguage={selectedLanguage}
                onLanguageChange={setSelectedLanguage}
                threadId={threadId}
                selectedLevel={selectedLevel}
            />
        );
    }

    return (
        <BackgroundStack className="flex h-screen" sidebarExpanded={sidebarExpanded}>
            <div className="flex min-h-0 w-full min-w-0 flex-1 items-stretch gap-2.5 p-2.5 pl-2.5 pr-1">
            <div className="sim-main-elevated flex min-h-0 min-w-0 flex-1 flex-col relative overflow-hidden">
            {/* ─── Zona principal: Audio Hero (Capa 06) ─── */}

                {/* Header */}
                <div className="absolute top-0 left-0 right-0 bg-white border-b border-stone-200 z-10">
                    <div className="flex items-center justify-between px-6 py-3">
                        <div className="flex items-center gap-3">
                            <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: '#1A4731' }}>
                                <span className="text-white font-bold text-xs">S</span>
                            </div>
                            <span className="font-semibold text-stone-800 text-sm">SimAI</span>
                            <span className="text-stone-400 text-sm">|</span>
                            <span className="text-stone-500 text-sm">{t('idbAse')}</span>
                        </div>
                        <div className="flex items-center gap-3">
                            <span className="flex items-center gap-1.5 text-xs font-medium px-3 py-1 rounded-full border border-emerald-300 text-emerald-700 bg-emerald-50">
                                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                                {t('live')}
                            </span>
                            <LanguageSelector
                                selectedLanguage={selectedLanguage}
                                onLanguageChange={setSelectedLanguage}
                                disabled={isLoading}
                            />
                            <button
                                onClick={handleBackToScenarios}
                                className="flex items-center gap-1.5 text-xs text-stone-500 hover:text-stone-800 px-3 py-1.5 rounded-lg hover:bg-stone-100 transition-colors"
                                title={t('restart')}
                            >
                                <RotateCcw className="w-3.5 h-3.5" />
                                {t('restart')}
                            </button>
                            <button
                                data-tour="header-help"
                                onClick={() => setShowHelpModal(true)}
                                className="p-2 text-stone-400 hover:text-stone-600 rounded-lg transition-colors"
                            >
                                <HelpCircle className="w-4 h-4" />
                            </button>
                            <Link
                                to="/admin"
                                className="p-2 text-stone-400 hover:text-stone-600 rounded-lg transition-colors"
                                title="Gestor de contenido"
                            >
                                <Settings className="w-4 h-4" />
                            </Link>
                        </div>
                    </div>
                    {/* Progress bar */}
                    <div className="px-6 pb-2 flex items-center gap-4">
                        <span className="text-[11px] text-stone-400 whitespace-nowrap">
                            Desafío {(challengePoolDetail?.filter(c => c.status === 'completed').length || 0) + (challengePoolDetail?.find(c => c.status === 'active') ? 1 : 0)} de {challengePoolDetail?.length || 2}
                        </span>
                        <div className="flex-1 h-1.5 bg-stone-200 rounded-full overflow-hidden">
                            <div
                                className="h-full rounded-full transition-all duration-700"
                                style={{
                                    width: `${Math.round(Math.max(0, Math.min(1, progress || 0)) * 100)}%`,
                                    background: 'linear-gradient(90deg, #1A4731, #2D7A4F)'
                                }}
                            />
                        </div>
                        <span className="text-xs font-semibold text-stone-700">{Math.round(Math.max(0, Math.min(1, progress || 0)) * 100)}%</span>
                    </div>
                </div>

                {/* Personaje + Audio Hero — zona scrollable entre header e input */}
                <div className="absolute inset-0 overflow-y-auto pt-[80px] pb-16 z-0">
                <div className="flex flex-col items-center max-w-lg w-full px-6 mx-auto min-h-full justify-center py-6">

                    {/* Briefing inicial */}
                    {isFirstMessage && messages.length === 0 && !briefingDismissed && scenarioInfo && tutorialCompleted && !tutorialRun && (
                        <div className="w-full mb-6 rounded-2xl bg-white border border-stone-200 p-5 shadow-sm animate-fade-in">
                            <div className="flex items-center gap-2 mb-3">
                                <MapPin className="w-4 h-4" style={{ color: '#1A4731' }} />
                                <span className="text-[10px] uppercase tracking-[0.2em] text-stone-400 font-semibold">{t('visitContext')}</span>
                            </div>
                            {scenarioInfo.tipo_proyecto && (
                                <p className="text-[11px] text-stone-400 mb-2">{scenarioInfo.tipo_proyecto}</p>
                            )}
                            <ScenarioDescription
                                text={scenarioInfo.descripcion_detallada || scenarioInfo.descripcion_breve}
                                className="text-[12px] text-stone-600 mb-4"
                            />
                            <div
                                className="rounded-xl p-4 mb-4 border-l-4"
                                style={{ background: '#E8F5EE', borderColor: '#1A4731' }}
                            >
                                <div className="flex items-center gap-2 mb-2">
                                    <div
                                        className="w-7 h-7 rounded-full flex items-center justify-center shrink-0 text-white text-xs font-bold"
                                        style={{ background: '#1A4731' }}
                                    >
                                        1
                                    </div>
                                    <p
                                        className="text-[11px] uppercase tracking-[0.18em] font-bold"
                                        style={{ color: '#1A4731' }}
                                    >
                                        {t('howToStart')}
                                    </p>
                                </div>
                                <p className="text-[13px] text-stone-700 leading-relaxed font-medium">
                                    {t('howToStartDesc')}
                                </p>
                            </div>
                            <div className="flex justify-end">
                                <button
                                    onClick={() => {
                                        setBriefingDismissed(true);
                                        // Saludo silencioso para que el personaje
                                        // abra la conversacion sin que el "hola"
                                        // aparezca en el chatbox.
                                        const greeting = selectedLanguage === 'en' ? 'hi' : 'hola';
                                        sendMessage(greeting, { silent: true });
                                    }}
                                    className="flex items-center gap-1.5 text-sm font-semibold text-white px-5 py-2 rounded-lg transition-all hover:opacity-90 hover:scale-[1.02] shadow-sm"
                                    style={{ background: '#1A4731' }}
                                >
                                    {t('understood')}
                                    <ArrowRight className="w-4 h-4" />
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Avatar */}
                    <div data-tour="avatar" className="relative mb-4">
                        {/* Hint overlay del coach (fade-in/fade-out) */}
                        {hint && (
                            <div
                                className="absolute z-20 right-full mr-4 top-0 w-64 bg-white rounded-2xl shadow-xl border border-stone-200 p-4 transition-all duration-500 ease-in-out"
                                style={{
                                    minWidth: '220px',
                                    opacity: hintOverlayVisible ? 1 : 0,
                                    transform: hintOverlayVisible ? 'translateY(0)' : 'translateY(-8px)',
                                    pointerEvents: hintOverlayVisible ? 'auto' : 'none',
                                }}
                            >
                                <div className="flex items-center gap-2 mb-2">
                                    <Lightbulb className="w-4 h-4 text-amber-500 shrink-0" />
                                    <span className="text-[10px] font-bold uppercase tracking-widest text-stone-400">{t('coachHint')}</span>
                                    <button
                                        onClick={() => { setHintOverlayVisible(false); if (hintTimerRef.current) clearTimeout(hintTimerRef.current); }}
                                        className="ml-auto text-stone-300 hover:text-stone-500 transition-colors"
                                    >
                                        <X className="w-3 h-3" />
                                    </button>
                                </div>
                                <p className="text-sm text-stone-700 leading-relaxed">{hint}</p>
                            </div>
                        )}

                        {AVATAR_ENGINE === 'rive' ? (
                            <RiveAvatar
                                isSpeaking={isTtsPlaying}
                                isListening={isLocalSTTActive}
                                isThinking={isLoading || isTtsLoading}
                            />
                        ) : (
                            <AnimatedAvatar
                                isSpeaking={isTtsPlaying}
                                isListening={isLocalSTTActive}
                                isThinking={isLoading || isTtsLoading}
                            />
                        )}
                    </div>

                    {/* Nombre y rol */}
                    <h2 className="text-2xl font-bold text-stone-900 mb-0.5 tracking-tight">
                        {scenarioInfo?.nombre_personaje || t('yourInterlocutor')}
                    </h2>
                    <p className="text-sm text-stone-500 mb-4">
                        {scenarioInfo?.cargo_personaje
                            ? scenarioInfo.cargo_personaje.replace('·', '•')
                            : scenarioInfo?.tipo_proyecto || ''}
                    </p>

                    {/* Status badge */}
                    <div className="mb-6">
                        {isTtsPlaying ? (
                            <span className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
                                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                                {t('speaking')}
                            </span>
                        ) : isLocalSTTActive ? (
                            <span className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
                                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                                {t('listening')}
                            </span>
                        ) : isLoading ? (
                            <span className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1 rounded-full bg-stone-100 text-stone-500 border border-stone-200">
                                <Loader2 className="w-3 h-3 animate-spin" />
                                {t('thinking')}
                            </span>
                        ) : null}
                    </div>

                    {/* Etiquetas de contexto */}
                    {(stance || visitStage || activeSceneMoment || bodyLanguage || tutorialForceAvatarLabels) && (() => {
                        const hideStance = currentPhase === 'recepcion';
                        const displayStance = hideStance ? '' : (stance || (tutorialForceAvatarLabels ? 'supportive' : ''));
                        const displayVisitStage = visitStage || (tutorialForceAvatarLabels ? 'recorrido' : '');
                        const displaySceneMoment = activeSceneMoment || (tutorialForceAvatarLabels ? 'Se levantan e inician el recorrido por las instalaciones.' : '');
                        const displayBodyLanguage = bodyLanguage || (tutorialForceAvatarLabels ? 'Sonríe con amabilidad mientras te invita a pasar al recibidor.' : '');
                        return (
                            <div data-tour="avatar-labels" className="w-full flex flex-col items-center">
                                {(displayStance || displayVisitStage) && (
                                    <div className="flex items-center gap-2 mb-3 flex-wrap justify-center">
                                        {displayStance && (
                                            <span className={`text-[11px] font-medium px-4 py-1.5 rounded-full border ${getStanceColor(displayStance)}`}>
                                                {getStanceLabel(displayStance)}
                                            </span>
                                        )}
                                        {displayVisitStage && (
                                            <span className="text-[11px] font-medium px-4 py-1.5 rounded-full border border-stone-300 bg-stone-100 text-stone-600 flex items-center gap-1.5">
                                                <MapPin className="w-3 h-3" />
                                                {getVisitStageLabel(displayVisitStage)}
                                            </span>
                                        )}
                                    </div>
                                )}
                                {displayBodyLanguage && (
                                    <div data-tour="body-language" className="w-full bg-white border border-stone-200 rounded-xl px-5 py-3.5 mb-4 shadow-sm">
                                        <div className="flex items-start gap-2.5">
                                            <Eye className="w-4 h-4 text-stone-400 mt-0.5 shrink-0" />
                                            <p className="text-[13px] text-stone-600 italic leading-relaxed">{displayBodyLanguage}</p>
                                        </div>
                                    </div>
                                )}
                                {displaySceneMoment && (
                                    <div className="w-full mb-5 rounded-xl border border-amber-200 bg-amber-50 px-5 py-3 animate-fade-in">
                                        <div className="flex items-start gap-2.5">
                                            <Film className="w-3.5 h-3.5 text-amber-600 mt-0.5 shrink-0" />
                                            <p className="text-[12px] text-amber-800 italic leading-relaxed">{displaySceneMoment}</p>
                                            {!tutorialForceAvatarLabels && activeSceneMoment && (
                                                <button onClick={() => { if (sceneMomentTimerRef.current) clearTimeout(sceneMomentTimerRef.current); setActiveSceneMoment(''); }} className="ml-auto p-0.5 text-amber-400 hover:text-amber-600 transition-colors shrink-0" aria-label="Cerrar">
                                                    <X className="w-3 h-3" />
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        );
                    })()}

                    {/* Banner: micrófono bloqueado por el navegador */}
                    {micPermissionDenied && (
                        <div className="w-full max-w-md mt-4 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3">
                            <div className="flex items-start gap-2.5">
                                <div className="w-7 h-7 rounded-full bg-amber-500 text-white flex items-center justify-center shrink-0 text-xs font-bold">!</div>
                                <div className="flex-1 min-w-0">
                                    <p className="text-[13px] font-semibold text-amber-900">{t('micPermissionTitle')}</p>
                                    <p className="text-[12px] text-amber-800 mt-1 leading-relaxed">{t('micPermissionBody')}</p>
                                    <div className="flex gap-2 mt-2">
                                        <button
                                            onClick={() => { setMicPermissionDenied(false); handleMicClick(); }}
                                            className="text-[11px] font-semibold text-white px-3 py-1 rounded-md hover:opacity-90"
                                            style={{ background: '#1A4731' }}
                                        >
                                            {t('micPermissionRetry')}
                                        </button>
                                        <button
                                            onClick={() => setMicPermissionDenied(false)}
                                            className="text-[11px] font-semibold text-amber-800 px-3 py-1 rounded-md border border-amber-300 hover:bg-amber-100"
                                        >
                                            {t('micPermissionDismiss')}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Botón de micrófono con anillo orbital */}
                    <div className="relative mt-4 mb-6 flex flex-col items-center">
                        {isLocalSTTActive && (
                            <div className="mb-2 flex items-center gap-1.5">
                                <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
                                <span className="text-xs font-semibold uppercase tracking-wider text-red-600">
                                    {t('recording')}
                                </span>
                            </div>
                        )}
                        <div className="relative">
                            {/* Halo exterior que respira */}
                            {!isInFeedbackPhase() && !isFeedbackCompleted() && <div className="mic-halo" />}
                            {/* Anillo orbital giratorio */}
                            {!isInFeedbackPhase() && !isFeedbackCompleted() && <div className="mic-orbit-ring" />}
                            <button
                                data-tour="mic"
                                onClick={handleMicClick}
                                disabled={isInFeedbackPhase() || isFeedbackCompleted()}
                                className={`relative w-20 h-20 rounded-full flex items-center justify-center transition-all duration-300 ${
                                    isLocalSTTActive
                                        ? 'shadow-[0_0_40px_rgba(239,68,68,0.3)]'
                                        : isInFeedbackPhase() || isFeedbackCompleted()
                                            ? 'opacity-40 cursor-not-allowed'
                                            : 'shadow-[0_0_30px_rgba(26,71,49,0.15)] hover:scale-105'
                                }`}
                                style={{
                                    background: isLocalSTTActive
                                        ? 'linear-gradient(145deg, #ef4444, #dc2626)'
                                        : (isInFeedbackPhase() || isFeedbackCompleted())
                                            ? '#D6D0C4'
                                            : 'linear-gradient(145deg, #2D5016, #1A4731)'
                                }}
                            >
                                <Mic className="w-7 h-7 text-white" />
                            </button>
                        </div>
                    </div>

                    {/* Dot wave animation */}
                    <div className="dot-wave mb-3 mt-2">
                        {[1,2,3,4,5].map(i => <div key={i} className="dot-bar" />)}
                    </div>

                    <p className="text-sm font-semibold text-stone-800 mb-0.5">
                        {isLoading
                            ? t('thinkingDots')
                            : isTtsLoading
                                ? t('preparingAudio')
                                : isLocalSTTActive
                                    ? t('listeningDots')
                                    : isTtsPlaying
                                        ? t('representativeSpeaking')
                                        : t('speakWhenReady')}
                    </p>
                    <p className="text-xs text-stone-400 mb-2">
                        {t('pressSpace', { key: '' })}<span className="px-1.5 py-0.5 bg-white border border-stone-200 rounded text-stone-500 text-[10px] font-mono">{t('spaceKey')}</span>
                    </p>

                    {/* Detener audio */}
                    {(isTtsPlaying || tutorialForceStopAudio) && (
                        <button
                            data-tour="stop-audio"
                            onClick={stopTTS}
                            className="text-[11px] text-stone-500 hover:text-stone-800 border border-stone-300 rounded-full px-4 py-1.5 hover:bg-white transition-all mb-3"
                        >
                            {t('stopAudio')}
                        </button>
                    )}
                </div>
                </div>{/* /scrollable hero zone */}

                {/* ─── Chat flotante (bottom-left) ─── */}
                <div className="absolute bottom-4 left-4 z-20">
                    <button
                        data-tour="chat-toggle"
                        onClick={() => setIsChatOpen(!isChatOpen)}
                        className="relative w-12 h-12 rounded-xl flex items-center justify-center bg-white border border-stone-200 shadow-md hover:shadow-lg transition-all hover:scale-105"
                    >
                        <MessageCircle className="w-5 h-5 text-stone-600" />
                        {messages.length > 0 && (
                            <span className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] rounded-full text-[10px] font-bold text-white flex items-center justify-center px-1" style={{ background: '#1A4731' }}>
                                {messages.filter(m => m.role !== 'scene').length}
                            </span>
                        )}
                    </button>
                </div>

                {/* Chat panel expandido */}
                {isChatOpen && (
                    <div className="absolute bottom-20 left-4 w-[500px] max-w-[calc(100vw-120px)] bg-white rounded-2xl border border-stone-200 shadow-2xl z-20 animate-fade-in overflow-hidden">
                        <div className="p-4 border-b border-stone-100 flex items-center justify-between">
                            <div>
                                <span className="text-[10px] uppercase tracking-widest font-bold text-stone-400">{t('conversation')}</span>
                                <p className="text-xs text-stone-500">{messages.filter(m => m.role !== 'scene').length} turnos · Desafío {(challengePoolDetail?.filter(c => c.status === 'completed').length || 0) + (challengePoolDetail?.find(c => c.status === 'active') ? 1 : 0)} de {challengePoolDetail?.length || 2}</p>
                            </div>
                            <button onClick={() => setIsChatOpen(false)} className="p-1 text-stone-400 hover:text-stone-600 transition-colors">
                                <X className="w-4 h-4" />
                            </button>
                        </div>
                        <div ref={chatContainerRef} className="max-h-[45vh] overflow-y-auto p-4 space-y-3">
                            {messages.map((message, index) => (
                                <ChatMessage key={index} message={message} />
                            ))}
                            {isLoading && (
                                <div className="flex justify-start">
                                    <div className="flex items-center gap-2 px-4 py-2.5 bg-stone-100 rounded-xl">
                                        <div className="w-1.5 h-1.5 bg-stone-400 rounded-full animate-bounce" />
                                        <div className="w-1.5 h-1.5 bg-stone-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }} />
                                        <div className="w-1.5 h-1.5 bg-stone-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }} />
                                    </div>
                                </div>
                            )}
                            <div ref={messagesEndRef} />
                        </div>
                    </div>
                )}

                {/* Input de texto (bottom) */}
                <div className="absolute bottom-0 left-0 right-0 px-6 pb-4 z-10">
                    {!isInitialized ? (
                        <div className="flex items-center justify-center p-3 bg-white border border-stone-200 rounded-xl max-w-lg mx-auto">
                            <Loader2 className="w-4 h-4 text-stone-400 animate-spin mr-2" />
                            <span className="text-xs text-stone-400">{t('initializing')}</span>
                        </div>
                    ) : isInFeedbackPhase() ? (
                        <div className="p-3.5 bg-amber-50 border border-amber-200 rounded-xl text-center flex items-center justify-center gap-2 max-w-lg mx-auto">
                            <Loader2 className="w-3.5 h-3.5 text-amber-600 animate-spin" />
                            <span className="text-xs text-amber-700">{t('generatingFeedback')}</span>
                        </div>
                    ) : (
                        <form data-tour="text-input" onSubmit={handleSubmit} className="flex gap-2 items-end max-w-lg mx-auto">
                            <div className="flex-1 relative">
                                <textarea
                                    ref={inputRef}
                                    rows={1}
                                    value={inputMessage}
                                    onChange={(e) => { setInputMessage(e.target.value); setIsVoiceTranscribing(false); }}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' && !e.shiftKey) {
                                            e.preventDefault();
                                            if (!isLoading && inputMessage.trim()) sendMessage(inputMessage);
                                        }
                                    }}
                                    placeholder={isLocalSTTActive ? t('transcribing') : t('writeTo')}
                                    disabled={isLoading}
                                    className={`w-full py-3 px-4 pr-8 rounded-xl text-sm focus:outline-none focus:ring-2 disabled:opacity-50 transition-all resize-none leading-relaxed ${
                                        isLocalSTTActive || isVoiceTranscribing
                                            ? 'bg-emerald-50 border border-emerald-300 text-stone-800 placeholder-emerald-400 focus:ring-emerald-300'
                                            : 'bg-white border border-stone-300 text-stone-800 placeholder-stone-400 focus:ring-stone-400'
                                    }`}
                                />
                                {(isLocalSTTActive || isVoiceTranscribing) && (
                                    <div className="absolute right-3 top-3 flex items-center gap-1">
                                        <div className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse" />
                                        <div className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse" style={{animationDelay: '0.2s'}} />
                                        <div className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse" style={{animationDelay: '0.4s'}} />
                                    </div>
                                )}
                                {inputMessage && !isLocalSTTActive && !isVoiceTranscribing && (
                                    <button type="button" onClick={() => { setInputMessage(''); setIsVoiceTranscribing(false); }} className="absolute right-2 top-2.5 p-0.5 text-stone-400 hover:text-stone-600 transition-colors">
                                        <XCircle className="w-4 h-4" />
                                    </button>
                                )}
                            </div>
                            <button
                                type="submit"
                                disabled={isLoading || !inputMessage.trim()}
                                className="px-4 py-3 text-white rounded-xl disabled:opacity-20 disabled:cursor-not-allowed transition-all hover:opacity-90 shrink-0"
                                style={{ background: '#1A4731' }}
                            >
                                <Send className="w-4 h-4" />
                            </button>
                        </form>
                    )}
                </div>
            </div>

            {/* ─── Panel lateral derecho ─── */}
            <Sidebar
                threadId={threadId}
                contentLocale={selectedLanguage}
                currentPhase={currentPhase}
                scenarioInfo={scenarioInfo}
                currentSkill={currentSkill}
                evaluatedSkills={evaluatedSkills}
                pendingSkills={pendingSkills}
                challengePoolDetail={challengePoolDetail}
                criteriaDetail={criteriaDetail}
                hint={hint}
                hintHistory={hintHistory}
                sessionStartTime={sessionStartRef.current}
                turnosDesafio={turnosDesafio}
                turnosMinimos={turnosMinimos}
                turnosMaximos={turnosMaximos}
                progress={progress}
                selectedSkills={selectedSkills}
                tutorialForceHint={tutorialForceHint}
                tutorialForceTimeline={tutorialForceTimeline}
                visitStage={visitStage}
                sceneMomentsShown={sceneMomentsShown}
                currentSceneMomentId={sceneMomentId}
                onLoadMessages={handleLoadMessages}
                onClearHistory={handleClearHistory}
                onNewThread={handleNewThread}
                onBackToScenarios={handleBackToScenarios}
                onShowHint={() => {
                    setHintOverlayVisible(true);
                    if (hintTimerRef.current) clearTimeout(hintTimerRef.current);
                    hintTimerRef.current = setTimeout(() => setHintOverlayVisible(false), 20000);
                }}
                hintOverlayVisible={hintOverlayVisible}
                onExpandedChange={setSidebarExpanded}
            />

            </div>

            {/* Tutorial de onboarding */}
            {isInitialized && selectedScenario && threadId && (
                <ChatTutorial
                    run={tutorialRun}
                    stepIndex={tutorialStepIndex}
                    onStepChange={handleTutorialStepChange}
                />
            )}

            {/* Modal de ayuda */}
            {showHelpModal && (
                <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50">
                    <div className="bg-white border border-stone-200 rounded-2xl p-6 max-w-2xl w-full mx-4 max-h-[80vh] overflow-y-auto shadow-2xl">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-xl font-semibold text-stone-900">{t('howItWorks')}</h3>
                            <button onClick={() => setShowHelpModal(false)} className="p-1.5 text-stone-400 hover:text-stone-600 hover:bg-stone-100 rounded-lg transition-all">
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        <div className="text-sm text-stone-600 mb-3">
                            {t('helpIntro', {
                                voice: `<b>${t('voice')}</b>`,
                                text: `<b>${t('text')}</b>`,
                            }).split(/<b>|<\/b>/).map((part, i) =>
                                i % 2 === 1
                                    ? <span key={i} className="text-stone-900 font-medium">{part}</span>
                                    : part
                            )}
                        </div>
                        <div className="flex items-start gap-2.5 px-4 py-3 rounded-xl mb-4" style={{ background: '#E8F5EE' }}>
                            <span className="text-lg leading-none mt-0.5">💡</span>
                            <p className="text-sm font-medium" style={{ color: '#1A4731' }}>{t('helpSoftSkillsNote')}</p>
                        </div>

                        <div className="space-y-3 text-sm">
                            <div className="border-l-4 border-blue-400 pl-4">
                                <p className="font-medium text-blue-700">{t('phase1')}</p>
                                <p className="text-stone-500">{t('phase1Desc')}</p>
                            </div>
                            <div className="border-l-4 border-amber-400 pl-4">
                                <p className="font-medium text-amber-700">{t('phase2')}</p>
                                <p className="text-stone-500">{t('phase2Desc')}</p>
                            </div>
                            <div className="border-l-4 pl-4" style={{ borderColor: '#1A4731' }}>
                                <p className="font-medium" style={{ color: '#1A4731' }}>{t('phase3')}</p>
                                <p className="text-stone-500">{t('phase3Desc')}</p>
                            </div>
                        </div>

                        <div className="mt-4 flex items-center justify-between gap-3">
                            <button onClick={resetTutorial} className="px-4 py-2 text-sm text-stone-500 border border-stone-300 rounded-lg hover:bg-stone-50 transition-colors">
                                {t('restartTutorial')}
                            </button>
                            <button onClick={() => setShowHelpModal(false)} className="px-4 py-2 text-white rounded-lg hover:opacity-90 transition-colors text-sm" style={{ background: '#1A4731' }}>
                                {t('understood')}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </BackgroundStack>
    );
}

export default function App() {
    const [lang, setLang] = useState('es');
    return (
        <I18nProvider lang={lang}>
            <OpenInNewTabBanner />
            <AppInner onLangChange={setLang} />
        </I18nProvider>
    );
}
